import { LoyaltyChallengeConfig } from '@shared/models';

import {
  BARCODE_FORMAT,
  DISPLAY_CONDITION,
  ProductCheckTabType,
  RewardFormat,
  ScanCheckMode,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const STELLAR_CONFIG: LoyaltyChallengeConfig = {
  currency: 'USD',
  appIcon: 'assets/images/stellar/favicon.ico',
  cssVariables: {
    'font-family-regular': 'Outfit-Regular',
    'font-family-medium': 'Outfit-Medium',
    'font-family-semibold': 'Outfit-SemiBold',
    'font-family-bold': 'Outfit-Bold',
    'font-family-extrabold': 'Outfit-ExtraBold',
    'font-family-black': 'Outfit-Black',

    'navbar-height': '70px',
    'navbar-background-color': '#F6F6F9',

    'nav-link-color': '#4B4F54',
    'nav-link-hover-color': '#4B4F54',
    'nav-link-active-color': '#33333F',
    'nav-link-active-border-color': '#DA2029',

    'nav-item-bg-color': 'white',
    'nav-item-active-bg-color': 'white',
    'nav-item-font-family': 'var(--font-family)',
    'nav-item-hover-bg-color': 'rgb(0 0 0 / 10%)',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'challenge-primary-color': '#DA2029',

    'tooltip-text-color': 'white',
    'tooltip-background-color': '#DA2029',
  },
  rewardFormat: RewardFormat.PRICE,
  og: {
    title: 'myStaters | Stater Bros. Markets',
    description:
      'Shop your favorite brands with myStaters and earn up to $30 in rewards every month at Stater Bros. Markets.',
  },
  rewardTemplate: '{points}<span style="font-size: 14px;">⭐️</span>',
  pages: {
    availablePages: ['help', 'current', 'history'],
    help: {
      meta: {
        title: 'How It Works | myStaters | Stater Bros. Markets',
        keywords:
          'myStaters, Stater Bros, how it works, challenges, rewards, help',
        description:
          'Learn how myStaters works: select your monthly challenges, shop eligible products in-store or online, and earn rewards.',
      },
      header: {
        background: '#F7D4CF',
        goBackButton: {
          text: 'Back to MyChallenges',
          backgroundColor: '#D9191E',
          color: '#FFFFFF',
          fontFamily: 'var(--font-family-medium)',
          borderRadius: '4px',
        },
        bannerAlternativeText:
          'myStaters: shop your favorite brands to earn rewards',
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
          title: {
            color: '#4B4F54',
            text: 'How It Works',
            fontFamily: 'var(--font-family-bold)',
            textAlignLeft: true,
          },
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          label: 'Frequently Asked Questions',
          type: TitleType.TEXT,
          styles: {
            color: '#4B4F54',
            fontSize: '24px',
            fontFamily: 'var(--font-family-bold)',
          },
        },
        questions: [
          {
            title: 'What is MyStaters?',
            content:
              'MyStaters is our new shopping experience that’s more personal and more rewarding. Having a FREE MyStaters account unlocks the following exclusive benefits: MyChallenges, Digital Deals, and more to come.',
          },
          {
            title: 'How do I get signed up?',
            content:
              'You can sign up for a MyStaters account <a href="https://www.staterbros.com/signin" target="_blank" title="">here</a>. Please note that it can take up to 24 hours for your Digital ID to activate and be ready for use at checkout.',
          },
          {
            title: 'What is a Digital ID?',
            content:
              'Your Digital ID is your phone number, which you will use at checkout.',
          },
          {
            title: 'Is there a cost for signing up?',
            content: 'No, MyStaters is completely free to sign up.',
          },
          {
            title: 'What is MyChallenges?',
            content:
              'MyChallenges is a personalized reward program that rewards you for shopping the brands you love! Every month, MyStaters account holders can activate up to 5 challenges and earn up to $30 a month. Customers can then use the rewards they earned after the 3rd day of the following month. Rewards earned from MyChallenges become available for use 3 days after you complete your month-long MyChallenges. You will receive an email with your reward balance amount when it is ready to use.',
          },
          {
            title: 'How do I earn rewards?',
            content:
              'You can earn rewards by participating in MyChallenges. When logged in, you can view  your personalized challenges <a href="https://www.staterbros.com/mystaters/mychallenges" target="_blank" title="">here</a>. You must activate a challenge, then shop the eligible  products to receive credit towards your challenge. You’ll receive an email 3 days after your  month-long MyChallenges has ended with your total rewards earned.',
          },
          {
            title: 'How do I redeem/use our rewards?',
            content:
              'Rewards earned from MyChallenges become available for use 3 days after you complete your month-long MyChallenges. You will receive an email with your reward balance amount when it is ready to use.<br/><br/>After that, your rewards balance will be automatically applied to your next in-store or online  order. If your cart total is less than your rewards balance amount, only the applicable  amount will be used. You can view your current rewards balance at the bottom of your  paper receipt.',
          },
          {
            title: 'How do I view my earned rewards?',
            content:
              'You will be able to view your rewards prior to use via email. After checking out in-store,  your rewards balance will be visible on your paper receipt. Currently, this is the only way to  view what rewards are associated with your account.',
          },
          {
            title:
              'How can I view my rewards balance if I place a pickup or delivery order?',
            content:
              'You can view your current rewards balance at the bottom of your paper receipt.',
          },
          {
            title: 'Do the rewards I earn through MyChallenges expire?',
            content:
              'Yes, any unused rewards will expire at the end of the month.',
          },
          {
            title: 'Does my reward balance roll over to the next month?',
            content:
              'No, at this time, your reward balance will not roll over to the next month. ',
          },
          {
            title:
              'Does MyChallenges count the purchases made on third-party marketplaces? (e.g.  DoorDash, Instacart, UberEats, etc.)',
            content:
              'No, only online orders placed through staterbros.com will be eligible for MyChallenges.',
          },
          {
            title:
              'Can I use my earned rewards on third-party marketplaces? (e.g. DoorDash, Instacart,  UberEats, etc.) ',
            content:
              'No, earned rewards are only applicable on in-store and online orders through  staterbros.com.',
          },
          {
            title:
              'Can I participate in MyChallenges, even if I haven’t used last month’s rewards balance?',
            content: 'Yes, you can.',
          },
          {
            title:
              'Do I have to use MyChallenges to recieve the benefits of a MyStaters Account?',
            content:
              'No, MyChallenges are a perk of MyStaters. You will still be able to shop, use Digital  Deals, and place pickup and delivery orders without participating in MyChallenges.<br/><br/>However, activating MyChallenges is the ONLY way to receive rewards at this time.',
          },
          {
            title: 'What happens if I change my phone number?',
            content:
              'If you have changed your phone number, please update it in your account information.  After your number has been verified, it may take up to 24 hours for your new Digital ID to  update and work at checkout.',
          },
          {
            title: 'Will my rewards be transferred to my new phone number?',
            content:
              'Yes, your earned rewards will be transferred from your old account to your updated  phone number. It may take up to 24 hours for your new Digital ID to update and work at  checkout.',
          },
          {
            title:
              'What if I don’t receive an email with my rewards information?',
            content:
              'Check your spam mail, if nothing is there, you may have previously unsubscribed from  our mailing list. You can resubscribe <a href="https://www.staterbros.com/page/email-signup" target="_blank" title="">here</a>. If you are still encountering issues, please  contact our Customer Care team at 1-855-STATERS or use our support portal <a href="https://www.staterbros.com/page/contact" target="_blank" title="">here</a>.',
          },
        ],
        accordion: {
          styles: {
            title: {
              fontSize: '16px',
              fontFamily: 'var(--font-family-bold)',
              color: '#4B4F54',
              lineHeight: '22px',
            },
            description: {
              fontSize: '14px',
              color: '#4B4F54',
              fontFamily: 'var(--font-family-regular)',
              lineHeight: '20px',
              margin: '0 10px',
            },
          },
          iconMore: 'assets/images/stellar/expand-more.svg',
          iconLess: 'assets/images/stellar/expand-less.svg',
          alternativeTextExpandMore: 'Show the answer',
          alternativeTextExpandLess: 'Hide the answer',
        },
      },
      extraLinks: [
        {
          title: {
            text: 'Need Help?',
            fontFamily: 'var(--font-family-bold)',
          },
          link: {
            color: 'white',
            label: 'Contact Us',
            fontFamily: 'var(--font-family-medium)',
            backgroundColor: '#D9191E',
            href: 'https://www.staterbros.com/page/contact',
          },
        },
      ],
    },
    currentChallenges: {
      meta: {
        title: 'MyChallenges | myStaters | Stater Bros. Markets',
        keywords:
          'myStaters, Stater Bros, MyChallenges, challenges, rewards, personalized offers',
        description:
          'Shop your favorite brands with myStaters and earn up to $30 in rewards every month at Stater Bros. Markets.',
      },
      background: 'white',
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family-medium)',
        backgroundColor: '#D9191E',
        containerBackgroundColor: 'white',
        text: 'How it Works',
        borderRadius: '8px',
        containerPadding: '2rem 0 0',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it Works',
            color: '#33333F',
            fontFamily: 'var(--font-family-semibold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: '#33333F',
          },
          skipNowButton: {
            text: 'Skip For Now',
            color: 'white',
            fontFamily: 'var(--font-family-medium)',
            backgroundColor: '#D9191E',
            borderRadius: '8px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
    },
    pastChallenges: {
      meta: {
        title: 'History | myStaters | Stater Bros. Markets',
        keywords:
          'myStaters, Stater Bros, history, past challenges, completed challenges, rewards',
        description:
          'Review the myStaters challenges you selected in the past and the rewards you earned at Stater Bros. Markets.',
      },
      banner: {
        visibility: true,
        title: 'Check your history',
        bannerAriaLabel:
          'History page displaying your past myStaters challenges',
        background: '#FFFFFF',
        styles: {
          title: {
            margin: 'unset',
            fontSize: '28px',
            color: '#4B4F54',
            padding: '2rem',
            textAlign: 'left',
            fontFamily: 'var(--font-family-bold)',
          },
          banner: {
            height: '130px',
            marginBottom: '2rem',
          },
        },
      },
      accordion: {
        variant: Variants.VARIANT_1,
        iconMore: 'assets/images/stellar/expand-more.svg',
        iconLess: 'assets/images/stellar/expand-less.svg',
        alternativeTextExpandMore: 'Expand More',
        alternativeTextExpandLess: 'Expand Less',
        date: {
          format: 'd MMMM yyyy',
          color: '#4B4F54',
          fontFamily: 'var(--font-family-semibold)',
          template: '{firstDate} - {lastDate}',
        },
        reward: {
          color: '#DA2029',
          fontFamily: 'var(--font-family-semibold)',
        },
      },
      noChallengesText: {
        color: '#4B4F54',
        fontFamily: 'var(--font-family-semibold)',
        text: 'No selected challenges',
      },
      noRewardsText: {
        color: '#4B4F54',
        fontFamily: 'var(--font-family-semibold)',
        text: 'No rewards earned',
      },
    },
    sorry: {
      background: '#F6F6F9',
      content: [
        '<div style="max-width: 200px; margin: 0 auto 1rem;"><img style="max-width: 100%;" src="assets/images/stellar/sorry-page-logo.webp" alt="Stater Bros. Markets"></div>',
        '<p style="font-size: 1.2rem; line-height: 24px; color: #4B4F54; font-family: var(--font-family-bold);">Sorry, we couldn’t load the page</p>',
        '<p style="font-size: 1rem; color: #4B4F54; font-family: var(--font-family-regular);">Please try again later.</p>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_15,
      backgroundColor: '#F7D4CF',
      bannerAriaLabel: 'myStaters: shop your favorite brands to earn rewards',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/stellar/cover-mobile.webp',
        },
        tablet: {
          imageUrl: 'assets/images/stellar/cover-mobile.webp',
        },
        desktop: {
          imageUrl: 'assets/images/stellar/cover-desktop.webp',
        },
      },
      campaignDate: {
        color: '#DA2029',
        fontFamily: 'var(--font-family-semibold)',
      },
      rewardRoundel: {
        imageUrl: 'assets/images/stellar/reward-roundel.svg',
        alternativeText: 'Earn up to $30 per month!',
        rewardAmount: {
          color: '#F6F6F1',
          fontFamily: 'var(--font-family-extrabold)',
          text: '$30',
        },
      },
      logo: {
        imageUrl: 'assets/images/stellar/myStaters-logo.svg',
        alternativeText: 'myStaters',
      },
      additionalInfo: {
        text: 'Shop your favorite\n brands to earn rewards!',
        color: '#DA2029',
        fontFamily: 'var(--font-family-extrabold)',
      },
    },
    brands: {
      variant: Variants.VARIANT_1,
      brandsBgColor: 'white',
      border: '2px solid #DA2029',
      alternativeText: 'favorite brands',
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        borderTop: '1px solid #f4f5f6',
        backgroundColor: '#F6F6F9',
      },
      linkStyles: {
        color: '#4B4F54',
        fontSize: '16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family-bold)',
      },
      links: [
        {
          url: 'help',
          label: 'Help Center',
        },
        {
          external: true,
          url: 'https://www.staterbros.com/page/privacy-policy',
          label: 'Privacy Policy',
        },
        {
          external: true,
          url: 'https://www.staterbros.com/page/mystaters-terms-and-conditions',
          label: 'Terms and Conditions',
        },
      ],
    },
    tutorial: {
      background: '#F7D4CF',
      carousel: {
        config: {
          loop: false,
          sectionAriaLabel: 'How it works',
          controlsAriaLabel: 'How it works navigation',
          paginationAriaLabel: 'How it works steps',
          dots: {
            inactiveBg: '#FFFFFF',
            activeBg: '#D9191E',
            ariaLabel: 'Go to step {variable} of {variable}',
          },
          slideIndicatorLabel: 'Step {variable} of {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/stellar/chevron-left.svg',
              alternativeText: 'Go to the previous step',
            },
            rightIcon: {
              imageUrl: 'assets/images/stellar/chevron-right.svg',
              alternativeText: 'Go to the next step',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_6,
          styles: {
            title: {
              color: '#4B4F54',
              fontFamily: 'var(--font-family-bold)',
            },
            description: {
              color: '#4B4F54',
              fontFamily: 'var(--font-family-regular)',
            },
            card: {
              border: 'none',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0px 4.269px 15.61px 0px rgba(38, 38, 38, 0.15)',
            },
          },
          steps: [
            {
              title: 'Activate',
              indicatorImage: {
                alternativeText: 'Step 1',
                imageUrl: 'assets/images/stellar/tuto-title-1.svg',
              },
              description:
                'View your monthly personalized challenges and select up to 5.',
              picture: {
                imageUrl: 'assets/images/stellar/step-1.webp',
                alternativeText: 'A challenge card with its Select button',
              },
            },
            {
              title: 'Shop',
              indicatorImage: {
                alternativeText: 'Step 2',
                imageUrl: 'assets/images/stellar/tuto-title-2.svg',
              },
              description:
                'Purchase eligible products in-store or online with your Digital ID.',
              picture: {
                imageUrl: 'assets/images/stellar/step-2.webp',
                alternativeText: 'A Stater Bros. basket full of groceries',
              },
            },
            {
              title: 'Earn',
              indicatorImage: {
                alternativeText: 'Step 3',
                imageUrl: 'assets/images/stellar/tuto-title-3.svg',
              },
              description:
                'You’ll receive an email 3 days after your challenge has ended with your total rewards earned.',
              picture: {
                imageUrl: 'assets/images/stellar/step-3.webp',
                alternativeText: 'Earn up to $30 per month!',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_3,
      navbar: {
        backgroundColor: '#F6F6F9',
        label: {
          color: '#4B4F54',
          text: 'Rewards Earned',
          fontFamily: 'var(--font-family-regular)',
        },
        amount: {
          color: '#4B4F54',
          fontFamily: 'var(--font-family-bold)',
        },
        tooltip: {
          iconUrl: 'assets/images/stellar/wallet-info-icon.svg',
          content: 'Credited by the 3rd of next month.',
          alternativeText: 'When your rewards are credited',
        },
      },
    },
    navbar: {
      variant: Variants.VARIANT_3,
      menuVariant: Variants.VARIANT_4,
      fontFamily: 'var(--font-family-regular)',
      dropdownLabel: 'Menu',
      hasLayoutPaddingTop: true,
      navbarAriaLabel: 'Main navigation',
      logo: {
        url: 'current',
        height: '100px',
        imageUrl: 'assets/images/stellar/retailer-logo.webp',
        alternativeText: 'Stater Bros. Markets, go to MyChallenges',
        showOnMobile: true,
        mobileHeight: '70px',
      },
      mobileMenuLink: {
        url: 'help',
        imageUrl: 'assets/images/stellar/help-mobile.svg',
        alternativeText: 'How it works',
      },
      items: [
        {
          url: 'current',
          label: 'MyChallenges',
          ariaLabel: 'MyChallenges',
          menuIcon: {
            alternativeText: 'Home Menu Icon',
            imageUrl: 'assets/images/stellar/home.svg',
          },
          iconUrlSelected: {
            alternativeText: 'Home Menu Icon Selected',
            imageUrl: 'assets/images/stellar/home-selected.svg',
          },
        },
        {
          url: 'history',
          label: 'History',
          ariaLabel: 'My History',
          menuIcon: {
            alternativeText: 'History Menu Icon',
            imageUrl: 'assets/images/stellar/history.svg',
          },
          iconUrlSelected: {
            alternativeText: 'History Menu Icon Selected',
            imageUrl: 'assets/images/stellar/history-selected.svg',
          },
        },
        {
          url: 'help',
          label: 'How it works',
          ariaLabel: 'How it works',
          menuIcon: {
            alternativeText: 'Help Menu Icon',
            imageUrl: 'assets/images/stellar/help.svg',
          },
          iconUrlSelected: {
            alternativeText: 'Help Menu Icon Selected',
            imageUrl: 'assets/images/stellar/help-selected.svg',
          },
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      backgroundColor: '#DA2029',
      showOnNavbarWhen: [],
      showAfterCampaignSummaryWhen: [],
      showBeforeCampaignSummaryWhen: [],
      showOnPages: 'all',
      titleAppearing: ['row', 'row', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#F6F7F1',
        fontFamily: 'var(--font-family-medium)',
      },
      title: {
        color: '#F6F7F1',
        showTitleOnMobile: true,
        fontFamily: 'var(--font-family-bold)',
        label: 'MyChallenges end in',
      },
      numberStyle: {
        color: '#DA2029',
        backgroundColor: '#F6F7F1',
        fontFamily: 'var(--font-family-extrabold)',
      },
      texts: {
        days: 'Days',
        hours: 'Hours',
        minutes: 'Minutes',
        seconds: 'Seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_9,
          backgroundColor: 'white',
          completedMsg: {
            color: '#DA2029',
            fontFamily: 'var(--font-family-black)',
            text: 'Completed!',
          },
          rewardProcessingMessage: {
            fontFamily: 'var(--font-family-medium)',
            color: '#DA2029',
            text: 'You’ll receive an email\n 3 days after your challenge has ended with your total rewards earned.',
          },
          right: {
            backgroundImage: {
              imageUrl: 'assets/images/stellar/reward-container.svg',
              alternativeText: '',
            },
            label: {
              text: 'You’ve earned',
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
            },
            amount: {
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },

        selectionButton: {
          variant: Variants.VARIANT_8,
          button: {
            color: 'white',
            label: 'Activate MyChallenge',
            backgroundColor: '#D9191E',
            title: 'Activate MyChallenge',
            fontFamily: 'var(--font-family-medium)',
          },
          offerLabel: {
            color: '#D9191E',
            fontFamily: 'var(--font-family-bold)',
            text: 'Earn {variable} in rewards!',
          },
          minimumSpendLabel: {
            color: '#4B4F54',
            fontFamily: 'var(--font-family-regular)',
            text: 'Minimum spend of ${variable} per visit',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_2,
          text: 'Deactivate',
          title: 'Deactivate this challenge',
          color: '#DA2029',
          fontFamily: 'var(--font-family-semibold)',
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(247, 212, 207, .90)',
          borderRadius: '8px',
          description: {
            text: 'Sorry, this challenge is no longer available.',
            textColor: '#4B4F54',
            fontFamily: 'var(--font-family-bold)',
            fontSize: '20px',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_2,
          bannerAlternativeText: 'Challenges banner',
          logoAlternativeText: 'Logo banner',
          cardStyles: {
            border: 'none',
            boxShadow: '#b1b1b1 0px 4px 24px',
          },
          header: {
            height: '143px',
            backgroundColor: 'white',
          },
          subHeader: {
            color: '#4B4F54',
            useDescription: false,
            backgroundColor: 'white',
            completedBackgroundColor: 'white',
            fontFamily: 'var(--font-family-medium)',
            fontSize: '12px',
          },
          body: {
            progressbar: {
              containerColor: '#EAEAEA',
              progressColor: '#DA2029',
              spendLabel: 'Spend',
              labelColor: '#4B4F54',
              spendLabelTop: '22px',
              earnLabel: 'Earn',
              earnLabelColor: '#DA2029',
            },
            rewards: {
              color: 'white',
              fontFamily: 'var(--font-family-bold)',
              defaultPath: 'assets/images/stellar/reward-rec.svg',
              bubbleHeight: '40px',
              alternativeText: {
                reached: 'I won ${variable}',
                unreached: 'I win ${variable}',
                default: 'I won ${variable}',
              },
              backgroundColors: {
                reached: { fill: '#DA2029', stroke: '#DA2029' },
                unreached: { fill: '#9999A5', stroke: '#9999A5' },
                default: { fill: '#F7D4CF', stroke: '#F7D4CF' },
              },
            },
            targets: {
              height: '20px',
              color: '#4B4F54',
              borderRadius: '5px',
              backgroundColor: '#F7D4CF',
              fontFamily: 'var(--font-family-medium)',
              targetUnReachedTitle: 'Spend ${variable} to reach this step',
              onIcon: {
                imageUrl: 'assets/images/stellar/picture-reached.svg',
                onIconAlternativeText: 'I win ${variable}',
              },
              offIcon: {
                imageUrl: 'assets/images/stellar/picture-reached.svg',
                offIconAlternativeText: 'I won ${variable}',
              },
            },
          },
          footer: {
            color: '#4B4F54',
            label: 'Total spent',
            fontFamily: 'var(--font-family-medium)',
            spentAmount: {
              color: '#4B4F54',
              borderRadius: '5px',
              backgroundColor: '#F7D4CF',
              fontFamily: 'var(--font-family-medium)',
            },
            productCheckButton: {
              ariaLabel: 'Click to check eligible products.',
              color: '#4B4F54',
              backgroundColor: 'white',
              label: 'Eligible Products',
              textDecoration: 'underline',
              fontFamily: 'var(--font-family-semibold)',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_11,
          header: {
            backgroundImage: {
              url: 'assets/images/stellar/frequency-banner.webp',
              bannerAriaLabel: 'A Stater Bros. basket full of groceries',
            },
            height: '143px',
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: 'white',
            title: {
              color: '#4B4F54',
              fontFamily: 'var(--font-family-bold)',
              label: 'Shop {variable} Times In-Store or Online',
            },
          },
          body: {
            minimumSpendLabel: {
              color: '#4B4F54',
              fontFamily: 'var(--font-family-regular)',
              text: 'Minimum spend of ${variable} per visit',
            },
            earnLabel: {
              color: '#D9191E',
              text: 'Earn $',
              suffix: 'in rewards!',
              fontFamily: 'var(--font-family-bold)',
            },
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/stellar/frequency-stamp-success.svg',
              iconNonSelected:
                'assets/images/stellar/frequency-stamp-empty.svg',
              nonActiveNumberColor: 'rgba(0, 46, 59, 0.4)',
              activeNumberColor: '#4B4F54',
            },
            progressBar: {
              activeColor: '#F7D4CF',
              defaultColor: '#C2C2CA',
            },
            visitsContainer: {
              defaultBorderColor: '#4B4F54',
              activeBorderColor: '#DA2029',
            },
            footer: {
              backgroundColor: 'white',
              remainingVisitsLabel: {
                color: '#4B4F54',
                text: 'Only {variable} more {visit|visits} to go.',
                fontFamily: 'var(--font-family-semibold)',
              },
              unselectLabel: {
                color: '#DA2029',
                fontFamily: 'var(--font-family-semibold)',
                text: 'Deactivate',
              },
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_1,
      stackOnMobile: true,
      title: {
        color: '#4B4F54',
        text: 'MyChallenges',
        fontFamily: 'var(--font-family-bold)',
        transform: 'none',
      },
      challengeSelection: {
        text: 'It may take 24 hours to reflect your progress.',
        defaultFontSize: '14px',
        color: '#4B4F54',
        fontFamily: 'var(--font-family-regular)',
      },
      challengeLabels: {
        inProgress: {
          color: '#4B4F54',
          text: 'In progress',
          fontFamily: 'var(--font-family-medium)',
        },
        onGoingLabel: {
          color: '#4B4F54',
          text: 'Activated',
          fontFamily: 'var(--font-family-medium)',
        },
        completedLabel: {
          color: '#4B4F54',
          text: 'Completed',
          fontFamily: 'var(--font-family-medium)',
        },
      },
      counterBorderRadius: '50px',
      challengesInfos: {
        color: '#4B4F54',
        backgroundColor: '#F6F7F1',
        fontFamily: 'var(--font-family-black)',
      },
      onGoingChallengesInfo: {
        color: '#4B4F54',
        backgroundColor: '#F7D4CF',
        fontFamily: 'var(--font-family-bold)',
      },
      completedChallengesInfo: {
        color: 'white',
        backgroundColor: '#DA2029',
        fontFamily: 'var(--font-family-extrabold)',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '8px',
        backgroundColor: '#DA2029',
        fontFamily: 'var(--font-family-medium)',
        ariaLabel: 'Close this dialog',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          backgroundColor: '',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/stellar/frequency-banner.webp',
            alternativeText: 'A Stater Bros. basket full of groceries',
          },
        },
        title: {
          color: '#33333F',
          fontFamily: 'var(--font-family-bold)',
          label: 'Are you sure?',
        },
        body: {
          color: '#4B4F54',
          fontFamily: 'var(--font-family-regular)',
          label: 'You can reactivate it later if you change your mind.',
        },
        actionButtons: {
          variant: Variants.VARIANT_2,
          confirmButton: {
            label: 'Yes, please deactivate.',
            borderRadius: '8px',
            textColor: '#FFFFFF',
            backgroundColor: '#DA2029',
            fontFamily: 'var(--font-family-medium)',
            ariaLabel: 'Deactivate this challenge',
          },
          closeButton: {
            label: 'Keep MyChallenge',
            borderRadius: '',
            textColor: '#33333F',
            backgroundColor: 'white',
            fontFamily: 'var(--font-family-medium)',
            ariaLabel: 'Keep this challenge',
          },
        },
      },
      productCheck: {
        ariaLabel: 'Check eligible products',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family)',
        tabs: [
          {
            type: ProductCheckTabType.SCAN,
            checkMode: ScanCheckMode.PRODUCT_LIST,
            label: 'Scan',
            title: 'Barcode Verification',
            readers: [BARCODE_FORMAT.EAN_8, BARCODE_FORMAT.EAN_13],
            successMessage: 'Success',
            errorMessage: 'NOT ELIGIBLE FOR THIS GAME',
            startButtonLabel: 'Start scanner',
            retryPermissionButtonLabel: 'Retry camera access',
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            restartButtonLabel: 'Restart Scan',
            errorWhenCameraPermissionDenied:
              'Please allow camera access to use the scanner.',
            errorWhenCameraNotFound: 'Camera not available',
            errorWhenCameraBusy:
              'The camera is in use by another app. Close it, then try again.',
            previewLabel: 'Barcode scanner preview',
            subtitle: 'Scan your product here',
          },
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            label: 'Enter manually',
            searchable: true,
            searchByLabel: true,
            inputPlaceholder: 'Search by product name or barcode',
            columns: {
              name: {
                header: 'Product',
                color: '',
                fontFamily: 'var(--font-family-regular)',
              },
              code: {
                header: 'Barcode',
                color: '',
                fontFamily: 'var(--font-family-regular)',
              },
            },
          },
        ],
        tabConfig: {
          activeColor: 'white',
          inactiveColor: 'black',
          inactiveBackground: 'white',
          fontFamily: 'var(--font-family)',
          inactiveBorderBottom: '2px solid #e6e6e6',
          activeBackground: 'var(--challenge-primary-color)',
          activeBorderBottom: '2px solid var(--challenge-primary-color)',
        },
      },
      firstSelection: {
        backgroundColor: 'white',
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/stellar/first-selection.svg',
            alternativeText: 'Trophy',
          },
        },
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Well Done! You’ve activated<br/>your first challenge.',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label: 'You can select {variable} more challenges.',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/stellar/last-selection.svg',
            alternativeText: 'Trophy',
          },
        },
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: "Congratulations, you've<br/>selected {variable} Challenges!",
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Use your myStaters account to shop in-store or online and earn rewards!',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/stellar/maximum-selection.svg',
            alternativeText: 'Alert',
          },
        },
        title: {
          color: '#272F35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Sorry, you’ve already activated<br/>{variable} challenges!',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Don’t worry, you can deactivate an unstarted challenge to pick another challenge.',
        },
      },
      outOfStockIssue: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Limit reached',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'This challenge has reached the maximum number of participants. Please choose another.',
        },
      },
      genericError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Error',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            "An error occurred, and the challenge couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Request timed out',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'Selecting the challenge took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: '#FFFFFF',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: '#33333F',
          fontFamily: 'var(--font-family-regular)',
          label:
            'This campaign has ended and challenges are no longer available for selection.',
        },
      },
    },
  },
};
